<template>
    <div class="sidebar-outline" :class="{closed: isClose, top: isTop}">
        <button type="button"
            id="btn-toggle"
            class="btn btn-outline-secondary btn-lg"
            :style="buttonStyle"
            :class="{closed: isClose, cloudy: isWriting}"
            @click="toggle()">
            <i class="fas fa-chart-line" v-if="isClose || (!isClose && !isTop)"></i>
            <i class="fas fa-times" v-else></i>
        </button>
        <div class="sidebar" id="content-sidebar" :class="{closed: isClose}">
            <div class="sidebar-body">
                <div class="sidebar-wrapper">
                    <div class="sticky" style="top:0">
                        <button type="button" class="btn btn-light btn-block btn-square" @click="toggle()">
                            閉じる
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div class="p-3" v-show="plan.book">
                        <div class="alert alert-danger" v-if="plan.required_log_based > 0">
                            この情報は小説情報を基準にしているため、正確ではありません。<br>
                            執筆記録基準で集計するには、あと{{plan.required_log_based}} 日以上の執筆が必要です。
                        </div>
                        <div class="text-center mt-4" v-if="plan.book">
                            <h4 class="mincho" v-html="$filters.ruby(plan.book.title + ' 執筆実績')"></h4>
                        </div>
                        <div class="row row-header">
                            <div class="col text-center">
                                <span class="badge badge-danger badge-indicator">
                                    <i class="far fa-calendar-check"></i>
                                </span>
                                <div class="small">
                                    執筆開始から
                                    <div class="indicator">
                                        {{ $filters.numberFormat(plan.progress) }}
                                    </div>
                                    日経過
                                </div>
                            </div>
                        </div>
                        <div class="row row-body">
                            <div class="col text-center">
                                <span class="badge badge-primary badge-indicator">
                                    <i class="fas fa-pen-nib"></i>
                                </span>
                                <div class="small">
                                    進捗
                                    <div class="indicator">
                                        {{ $filters.numberFormat(plan.written) }}
                                        /
                                        {{ $filters.numberFormat(plan.episodes) }}
                                    </div>
                                    話
                                </div>
                            </div>
                            <div class="col text-center">
                                <span class="badge badge-success badge-indicator">
                                    <i class="far fa-clock"></i>
                                </span>
                                <div v-if="plan.days_left" class="small">
                                    あと
                                    <div class="indicator">{{plan.days_left}}</div>
                                    日
                                    <div>（推定）</div>
                                </div>
                                <div v-else-if="plan.is_finished" class="text-success">完結済み</div>
                                <div v-else><i class="fas fa-question"></i></div>
                            </div>
                        </div>
                        <div class="row-body">
                            <div class="card">
                                <div class="card-body planner">
                                    <div class="row">
                                        <div class="col-md-3">
                                            <div class="caption">
                                                執筆パフォーマンス
                                            </div>
                                        </div>
                                        <div class="col-md-5">
                                            <span class="param">
                                            {{$filters.numberFormat(plan.performance)}}
                                            </span>
                                            文字/日
                                        </div>
                                        <div class="col-md-4 pt-2">
                                            <div>
                                                平均
                                                {{$filters.numberFormat(plan.characters_per_episode)}}
                                                文字/話
                                            </div>
                                            <div>
                                                合計
                                                {{$filters.numberFormat(plan.characters)}}
                                                文字
                                            </div>
                                            <div class="text-primary">
                                                あと
                                                {{$filters.numberFormat(plan.norma)}}
                                                文字執筆
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="mt-3">
                            <div class="card" :class="{
                                'text-white': plan.is_finished,
                                'bg-primary': plan.is_finished
                            }">
                                <div class="card-body planner">
                                    <div class="row" v-if="!plan.is_finished">
                                        <div class="col-md-3">
                                            <div class="caption">
                                                残り予想
                                            </div>
                                        </div>
                                        <div class="col-md-5">
                                            <span class="param">
                                            {{$filters.numberFormat(plan.unwritten)}}
                                            </span>
                                            文字
                                        </div>
                                        <div class="col-md-4 pt-2">
                                            <div>
                                                進捗
                                                <span v-if="plan.progress_rate">{{plan.progress_rate}}</span>
                                                <span v-else>？</span>
                                                %
                                            </div>
                                            <div>
                                                <div v-if="plan.estimate_completion_date">
                                                    <div class="small">
                                                        完了予定
                                                    </div>
                                                    <div class="text-primary">
                                                        {{$filters.dateFormat(plan.estimate_completion_date, 'YYYY年MM月DD日(dd)')}}
                                                    </div>
                                                </div>
                                                <span v-else>完了未定</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row" v-else>
                                        <div class="col">
                                            <div class="finished">
                                                {{$filters.dateFormat(plan.book.property.last_published_at, 'YYYY年MM月DD日')}}
                                                完結済み
                                                <i class="fas fa-check"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="mt-3 text-center text-warning" v-if="plan.writings && !plan.writings.length">
                            <i class="fas fa-exclamation-triangle"></i>
                            執筆実績がありません。
                        </div>
                        <div class="mt-3">
                            <canvas id="logs"></canvas>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style src="./sidebar.scss" scoped lang="scss"></style>
<style scoped lang="scss">
.badge-indicator {
    font-size: 2rem;
    border-radius: 50%;
    padding: 1rem 1.2rem;
    margin-bottom: 1rem;
}
.indicator {
    font-size: 2rem;
    font-weight: bold;
    line-height: 1rem;
    padding: .7rem 0;
}
.row-header {
    margin-top: 3rem;
}
.row-body {
    margin-top: 4rem;
}
.planner {
    .caption {
        font-size: .75rem;
        padding-top: 2rem;
    }
    .param {
        font-size: 2.5rem;
        font-weight: bold;
    }
    .finished {
        font-size: 2.2rem;
        font-weight: bold;
        text-align: center;
    }
}
</style>

<script>
import Chart from "chart.js/auto";
export default {
    props: {
        book_id: {
            required: true,
        },
        order: {
            default: 3
        },
        isWriting: {
            default: false,
        },
    },
    data() {
        return {
            plan: {},
            isClose: true,
            isTop: false,
            reloaded_at: null,
            isLoading: false,
        }
    },
    methods: {
        load() {
            if (this.isLoading) return;
            this.isLoading = true;
            if (this.reloaded_at && (this.book_id == this.plan.book.id)) {
                // 10秒以内の再集計はしない
                if (moment(this.reloaded_at).add(10, "s").isAfter(moment())) {
                    this.isLoading = false;
                    return;
                }
            }
            axios.get("/mypage/book/plan", {params: {id: this.book_id}}).then(res => {
                this.plan = res.data;
                // チャート情報作成
                if (! this.plan.is_finished && this.plan.writings && this.plan.writings.length > 0) {
                    try {
                        const dates = [];
                        for (let i = 0; i <= 30; i++) {
                            dates.push(moment().subtract(30 - i, "day").format("YYYY-MM-DD"));
                        }
                        const datasets = [];
                        const values = [];
                        for (const date of dates) {
                            const writing = _.find(this.plan.writings, {'written_date': date});
                            if (writing) {
                                values.push(writing.written_characters);
                            } else {
                                values.push(0);
                            }
                        }
                        datasets.push({
                            label: "執筆実績",
                            borderColor: "rgba(76, 211, 99, 1)",
                            backgroundColor: "rgba(211, 255, 211, .8)",
                            borderWidth: 1,
                            data: values,
                            yAxisID: 'yAxis',
                            xAxisID: 'xAxis'
                        });
                        const datelabels = [];
                        for (const date of dates) {
                            datelabels.push(moment(date).format("MM/DD"));
                        }
                        const data = {
                            labels: datelabels,
                            datasets: datasets
                        };
                        Chart.defaults.color = '#999';
                        Chart.defaults.font.size = 9;
                        if (!this.chart) {
                            this.chart = new Chart(document.getElementById("logs"), {
                                type: "bar",
                                data: data,
                                options: {
                                    scales: {
                                        yAxis: {
                                            grid: {
                                                color: "rgba(192, 192, 192, .5)"
                                            }
                                        },
                                        xAxis: {
                                            grid: {
                                                color: "rgba(192, 192, 192, .5)"
                                            }
                                        }
                                    },
                                    aspectRatio: 1.5
                                },
                            });
                        } else {
                            this.chart.data = data;
                            this.chart.update("none");
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }
                this.reloaded_at = moment();
                this.isLoading = false;
            }).catch(err => {
                alert.err(err);
                this.isLoading = false;
            });
        },
        toggle(isClose = null) {
            if (isClose !== null) {
                this.isClose = isClose;
                this.isTop = false;
            } else {
                if (!this.isClose && !this.isTop) {
                    this.isTop = true;
                } else {
                    this.isClose = !this.isClose;
                    this.isTop = !this.isClose;
                }
                this.$emit("toggle", this.isClose);
            }
        },
    },
    watch: {
        book_id(n, o) {
            this.load();
        }
    },
    computed: {
        buttonStyle() { return { "top": (this.order * 46 + 50) + "px" }; },
    },
    mounted() {
        this.load();
    }
}
</script>